import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, Search, Star, 
  CheckCircle, XCircle, Clock, FileText, MessageSquare,
  ChevronDown, Filter, MoreVertical, Save, X, Database, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons
const CATEGORY_ICONS = {
  vaccination: '💉',
  prevention: '🛡️',
  nutrition: '🥗',
  comportement: '🧠',
  urgences: '🚨',
  soins: '🩺',
  adoption: '🏠',
  autre: '📝'
};

const CATEGORIES = [
  { value: 'vaccination', label: 'Vaccination' },
  { value: 'prevention', label: 'Prévention' },
  { value: 'nutrition', label: 'Nutrition' },
  { value: 'comportement', label: 'Comportement' },
  { value: 'urgences', label: 'Urgences' },
  { value: 'soins', label: 'Soins' },
  { value: 'adoption', label: 'Adoption' },
  { value: 'autre', label: 'Autre' }
];

const AdminContentManager = ({ adminToken }) => {
  const [activeTab, setActiveTab] = useState('articles');
  const [isSeedingArticles, setIsSeedingArticles] = useState(false);
  
  // Articles state
  const [articles, setArticles] = useState([]);
  const [isLoadingArticles, setIsLoadingArticles] = useState(true);
  const [articleFilter, setArticleFilter] = useState({ status: '', category: '', search: '' });
  const [editingArticle, setEditingArticle] = useState(null);
  const [showArticleEditor, setShowArticleEditor] = useState(false);
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(true);
  const [reviewFilter, setReviewFilter] = useState({ status: '', search: '' });
  const [reviewStats, setReviewStats] = useState({ pending: 0, approved: 0, rejected: 0 });

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${adminToken}`
  };

  // Fetch data on mount and tab change
  useEffect(() => {
    if (activeTab === 'articles') {
      fetchArticles();
    } else {
      fetchReviews();
      fetchReviewStats();
    }
  }, [activeTab, articleFilter, reviewFilter]);

  // ==================== ARTICLES ====================

  const fetchArticles = async () => {
    setIsLoadingArticles(true);
    try {
      let url = `${API_URL}/api/conseils/admin/all?limit=50`;
      if (articleFilter.status) url += `&status=${articleFilter.status}`;
      if (articleFilter.category) url += `&category=${articleFilter.category}`;
      if (articleFilter.search) url += `&search=${encodeURIComponent(articleFilter.search)}`;
      
      const response = await fetch(url, { headers });
      if (response.ok) {
        setArticles(await response.json());
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Erreur lors du chargement des articles');
    }
    setIsLoadingArticles(false);
  };

  const handleCreateArticle = () => {
    setEditingArticle({
      title: '',
      category: 'autre',
      excerpt: '',
      cover_image: '',
      content_blocks: [{ type: 'text', content: '' }],
      status: 'draft',
      featured: false,
      meta_title: '',
      meta_description: '',
      related_species: [],
      tags: []
    });
    setShowArticleEditor(true);
  };

  const handleEditArticle = async (articleId) => {
    try {
      const response = await fetch(`${API_URL}/api/conseils/admin/${articleId}`, { headers });
      if (response.ok) {
        setEditingArticle(await response.json());
        setShowArticleEditor(true);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'article');
    }
  };

  const handleSaveArticle = async () => {
    try {
      const isNew = !editingArticle.article_id;
      const url = isNew 
        ? `${API_URL}/api/conseils/` 
        : `${API_URL}/api/conseils/${editingArticle.article_id}`;
      
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers,
        body: JSON.stringify(editingArticle)
      });
      
      if (response.ok) {
        toast.success(isNew ? 'Article créé' : 'Article mis à jour');
        setShowArticleEditor(false);
        setEditingArticle(null);
        fetchArticles();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleTogglePublish = async (article) => {
    try {
      const endpoint = article.status === 'published' ? 'unpublish' : 'publish';
      const response = await fetch(`${API_URL}/api/conseils/${article.article_id}/${endpoint}`, {
        method: 'POST',
        headers
      });
      
      if (response.ok) {
        toast.success(article.status === 'published' ? 'Article dépublié' : 'Article publié');
        fetchArticles();
      }
    } catch (error) {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const handleToggleFeatured = async (article) => {
    try {
      const response = await fetch(`${API_URL}/api/conseils/${article.article_id}/toggle-featured`, {
        method: 'POST',
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchArticles();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/conseils/${articleId}`, {
        method: 'DELETE',
        headers
      });
      
      if (response.ok) {
        toast.success('Article supprimé');
        fetchArticles();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSeedArticles = async () => {
    if (!window.confirm('Cette action va créer les articles par défaut dans la base de données. Les articles existants ne seront pas affectés. Continuer ?')) return;
    
    setIsSeedingArticles(true);
    try {
      const response = await fetch(`${API_URL}/api/conseils/seed-defaults`, {
        method: 'POST',
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchArticles();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors du seeding');
      }
    } catch (error) {
      console.error('Error seeding articles:', error);
      toast.error('Erreur lors de la création des articles');
    }
    setIsSeedingArticles(false);
  };

  // ==================== REVIEWS ====================

  const fetchReviews = async () => {
    setIsLoadingReviews(true);
    try {
      let url = `${API_URL}/api/avis/admin/all?limit=50`;
      if (reviewFilter.status) url += `&status=${reviewFilter.status}`;
      if (reviewFilter.search) url += `&search=${encodeURIComponent(reviewFilter.search)}`;
      
      const response = await fetch(url, { headers });
      if (response.ok) {
        setReviews(await response.json());
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
    setIsLoadingReviews(false);
  };

  const fetchReviewStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/avis/admin/stats`, { headers });
      if (response.ok) {
        setReviewStats(await response.json());
      }
    } catch (error) {
      console.error('Error fetching review stats:', error);
    }
  };

  const handleModerateReview = async (reviewId, status) => {
    try {
      const response = await fetch(`${API_URL}/api/avis/${reviewId}/moderate`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status })
      });
      
      if (response.ok) {
        toast.success(status === 'approved' ? 'Avis approuvé' : 'Avis refusé');
        fetchReviews();
        fetchReviewStats();
      }
    } catch (error) {
      toast.error('Erreur lors de la modération');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Supprimer cet avis ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/avis/${reviewId}`, {
        method: 'DELETE',
        headers
      });
      
      if (response.ok) {
        toast.success('Avis supprimé');
        fetchReviews();
        fetchReviewStats();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ==================== ARTICLE EDITOR ====================

  const ArticleEditor = () => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl my-8 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">
            {editingArticle?.article_id ? 'Modifier l\'article' : 'Nouvel article'}
          </h2>
          <button onClick={() => { setShowArticleEditor(false); setEditingArticle(null); }}>
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Form */}
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
            <Input
              value={editingArticle?.title || ''}
              onChange={(e) => setEditingArticle({...editingArticle, title: e.target.value})}
              placeholder="Titre de l'article"
            />
          </div>
          
          {/* Category & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
              <select
                value={editingArticle?.category || 'autre'}
                onChange={(e) => setEditingArticle({...editingArticle, category: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{CATEGORY_ICONS[cat.value]} {cat.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <select
                value={editingArticle?.status || 'draft'}
                onChange={(e) => setEditingArticle({...editingArticle, status: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="draft">Brouillon</option>
                <option value="published">Publié</option>
              </select>
            </div>
          </div>
          
          {/* Excerpt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Résumé (excerpt) *</label>
            <textarea
              value={editingArticle?.excerpt || ''}
              onChange={(e) => setEditingArticle({...editingArticle, excerpt: e.target.value})}
              placeholder="Un court résumé de l'article (affiché dans les listes)"
              rows={3}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          
          {/* Cover Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Image de couverture (URL)</label>
            <Input
              value={editingArticle?.cover_image || ''}
              onChange={(e) => setEditingArticle({...editingArticle, cover_image: e.target.value})}
              placeholder="https://..."
            />
          </div>
          
          {/* Content Blocks */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contenu</label>
            {editingArticle?.content_blocks?.map((block, index) => (
              <div key={index} className="mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <select
                    value={block.type}
                    onChange={(e) => {
                      const newBlocks = [...editingArticle.content_blocks];
                      newBlocks[index] = { type: e.target.value, content: '' };
                      setEditingArticle({...editingArticle, content_blocks: newBlocks});
                    }}
                    className="px-2 py-1 border rounded text-sm"
                  >
                    <option value="text">Texte</option>
                    <option value="image">Image</option>
                    <option value="quote">Citation</option>
                  </select>
                  <button
                    onClick={() => {
                      const newBlocks = editingArticle.content_blocks.filter((_, i) => i !== index);
                      setEditingArticle({...editingArticle, content_blocks: newBlocks});
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                
                {block.type === 'text' && (
                  <textarea
                    value={block.content || ''}
                    onChange={(e) => {
                      const newBlocks = [...editingArticle.content_blocks];
                      newBlocks[index].content = e.target.value;
                      setEditingArticle({...editingArticle, content_blocks: newBlocks});
                    }}
                    rows={5}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Contenu du paragraphe..."
                  />
                )}
                
                {block.type === 'image' && (
                  <div className="space-y-2">
                    <Input
                      value={block.url || ''}
                      onChange={(e) => {
                        const newBlocks = [...editingArticle.content_blocks];
                        newBlocks[index].url = e.target.value;
                        setEditingArticle({...editingArticle, content_blocks: newBlocks});
                      }}
                      placeholder="URL de l'image"
                    />
                    <Input
                      value={block.caption || ''}
                      onChange={(e) => {
                        const newBlocks = [...editingArticle.content_blocks];
                        newBlocks[index].caption = e.target.value;
                        setEditingArticle({...editingArticle, content_blocks: newBlocks});
                      }}
                      placeholder="Légende (optionnel)"
                    />
                  </div>
                )}
                
                {block.type === 'quote' && (
                  <div className="space-y-2">
                    <textarea
                      value={block.content || ''}
                      onChange={(e) => {
                        const newBlocks = [...editingArticle.content_blocks];
                        newBlocks[index].content = e.target.value;
                        setEditingArticle({...editingArticle, content_blocks: newBlocks});
                      }}
                      rows={3}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Texte de la citation..."
                    />
                    <Input
                      value={block.author || ''}
                      onChange={(e) => {
                        const newBlocks = [...editingArticle.content_blocks];
                        newBlocks[index].author = e.target.value;
                        setEditingArticle({...editingArticle, content_blocks: newBlocks});
                      }}
                      placeholder="Auteur (optionnel)"
                    />
                  </div>
                )}
              </div>
            ))}
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setEditingArticle({
                  ...editingArticle,
                  content_blocks: [...(editingArticle.content_blocks || []), { type: 'text', content: '' }]
                });
              }}
            >
              <Plus className="w-4 h-4 mr-1" />
              Ajouter un bloc
            </Button>
          </div>
          
          {/* Options */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editingArticle?.featured || false}
                onChange={(e) => setEditingArticle({...editingArticle, featured: e.target.checked})}
                className="rounded"
              />
              <span className="text-sm">Mettre en avant (featured)</span>
            </label>
          </div>
          
          {/* SEO */}
          <div className="pt-4 border-t">
            <h3 className="font-medium mb-3">SEO</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Meta title</label>
                <Input
                  value={editingArticle?.meta_title || ''}
                  onChange={(e) => setEditingArticle({...editingArticle, meta_title: e.target.value})}
                  placeholder="Titre SEO (laissez vide pour utiliser le titre)"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Meta description</label>
                <textarea
                  value={editingArticle?.meta_description || ''}
                  onChange={(e) => setEditingArticle({...editingArticle, meta_description: e.target.value})}
                  placeholder="Description SEO (laissez vide pour utiliser l'excerpt)"
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Tags (séparés par des virgules)</label>
                <Input
                  value={editingArticle?.tags?.join(', ') || ''}
                  onChange={(e) => setEditingArticle({
                    ...editingArticle, 
                    tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                  })}
                  placeholder="vaccin, chien, prévention"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <Button variant="outline" onClick={() => { setShowArticleEditor(false); setEditingArticle(null); }}>
            Annuler
          </Button>
          <Button onClick={handleSaveArticle} className="bg-green-600 hover:bg-green-700">
            <Save className="w-4 h-4 mr-1" />
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );

  // ==================== RENDER ====================

  return (
    <div className="p-6">
      {/* Sub-tabs */}
      <div className="flex items-center gap-4 mb-6 border-b pb-4">
        <button
          onClick={() => setActiveTab('articles')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'articles' 
              ? 'bg-green-100 text-green-700' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <FileText className="w-5 h-5" />
          Articles Conseils
        </button>
        <button
          onClick={() => setActiveTab('reviews')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'reviews' 
              ? 'bg-green-100 text-green-700' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <MessageSquare className="w-5 h-5" />
          Avis Utilisateurs
          {reviewStats.pending > 0 && (
            <span className="px-2 py-0.5 bg-orange-500 text-white text-xs rounded-full">
              {reviewStats.pending}
            </span>
          )}
        </button>
      </div>

      {/* Articles Tab */}
      {activeTab === 'articles' && (
        <div>
          {/* Header */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher un article..."
                  value={articleFilter.search}
                  onChange={(e) => setArticleFilter({...articleFilter, search: e.target.value})}
                  className="pl-10"
                />
              </div>
              <select
                value={articleFilter.status}
                onChange={(e) => setArticleFilter({...articleFilter, status: e.target.value})}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="">Tous les statuts</option>
                <option value="draft">Brouillons</option>
                <option value="published">Publiés</option>
              </select>
              <select
                value={articleFilter.category}
                onChange={(e) => setArticleFilter({...articleFilter, category: e.target.value})}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="">Toutes catégories</option>
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <Button onClick={handleCreateArticle} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-1" />
              Nouvel article
            </Button>
            <Button 
              onClick={handleSeedArticles} 
              variant="outline"
              disabled={isSeedingArticles}
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
            >
              {isSeedingArticles ? (
                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Database className="w-4 h-4 mr-1" />
              )}
              {isSeedingArticles ? 'Création...' : 'Peupler les articles'}
            </Button>
          </div>

          {/* Articles List */}
          {isLoadingArticles ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Aucun article trouvé</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Article</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Catégorie</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Statut</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Vues</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Date</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {articles.map((article) => (
                    <tr key={article.article_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {article.featured && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                          <div>
                            <p className="font-medium text-gray-800 line-clamp-1">{article.title}</p>
                            <p className="text-sm text-gray-500 line-clamp-1">{article.excerpt}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-gray-100 rounded text-sm">
                          {CATEGORY_ICONS[article.category]} {article.category}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          article.status === 'published' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {article.status === 'published' ? 'Publié' : 'Brouillon'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <Eye className="w-4 h-4 inline mr-1" />
                        {article.view_count || 0}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {article.published_at 
                          ? new Date(article.published_at).toLocaleDateString('fr-FR')
                          : new Date(article.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleToggleFeatured(article)}
                            className={`p-1.5 rounded hover:bg-gray-100 ${article.featured ? 'text-yellow-500' : 'text-gray-400'}`}
                            title={article.featured ? 'Retirer de la une' : 'Mettre en avant'}
                          >
                            <Star className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleTogglePublish(article)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-400"
                            title={article.status === 'published' ? 'Dépublier' : 'Publier'}
                          >
                            {article.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleEditArticle(article.article_id)}
                            className="p-1.5 rounded hover:bg-gray-100 text-blue-500"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteArticle(article.article_id)}
                            className="p-1.5 rounded hover:bg-gray-100 text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Reviews Tab */}
      {activeTab === 'reviews' && (
        <div>
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border p-4">
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold">{reviewStats.total || 0}</p>
            </div>
            <div className="bg-orange-50 rounded-xl border border-orange-100 p-4">
              <p className="text-sm text-orange-600">En attente</p>
              <p className="text-2xl font-bold text-orange-700">{reviewStats.pending || 0}</p>
            </div>
            <div className="bg-green-50 rounded-xl border border-green-100 p-4">
              <p className="text-sm text-green-600">Approuvés</p>
              <p className="text-2xl font-bold text-green-700">{reviewStats.approved || 0}</p>
            </div>
            <div className="bg-red-50 rounded-xl border border-red-100 p-4">
              <p className="text-sm text-red-600">Refusés</p>
              <p className="text-2xl font-bold text-red-700">{reviewStats.rejected || 0}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-2 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher un avis..."
                value={reviewFilter.search}
                onChange={(e) => setReviewFilter({...reviewFilter, search: e.target.value})}
                className="pl-10"
              />
            </div>
            <select
              value={reviewFilter.status}
              onChange={(e) => setReviewFilter({...reviewFilter, status: e.target.value})}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="approved">Approuvés</option>
              <option value="rejected">Refusés</option>
            </select>
          </div>

          {/* Reviews List */}
          {isLoadingReviews ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Aucun avis trouvé</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.review_id} className="bg-white rounded-xl border p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-medium">{review.user_name}</span>
                        <div className="flex gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
                            />
                          ))}
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          review.status === 'approved' ? 'bg-green-100 text-green-700' :
                          review.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {review.status === 'approved' ? 'Approuvé' : 
                           review.status === 'rejected' ? 'Refusé' : 'En attente'}
                        </span>
                      </div>
                      <h4 className="font-medium text-gray-800 mb-1">{review.title}</h4>
                      <p className="text-gray-600 text-sm">{review.comment}</p>
                      {review.vet_name && (
                        <p className="text-sm text-gray-400 mt-2">Vétérinaire: {review.vet_name}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        {new Date(review.created_at).toLocaleDateString('fr-FR', { 
                          day: 'numeric', month: 'long', year: 'numeric' 
                        })}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      {review.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleModerateReview(review.review_id, 'approved')}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approuver
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleModerateReview(review.review_id, 'rejected')}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Refuser
                          </Button>
                        </>
                      )}
                      <button
                        onClick={() => handleDeleteReview(review.review_id)}
                        className="p-2 rounded hover:bg-gray-100 text-gray-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Article Editor Modal */}
      {showArticleEditor && <ArticleEditor />}
    </div>
  );
};

export default AdminContentManager;
